import * as React from "react";
import {
    Bug,
    Cable,
    FileText,
    Handshake,
    LayoutDashboard,
    Shield,
} from "lucide-react";

import { NavMain } from "@/components/nav-main";
import {
    Sidebar,
    SidebarContent,
    SidebarHeader,
    SidebarRail,
} from "@/components/ui/sidebar";
import { useSidebar } from "@/components/ui/sidebar-context";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { LocalRoutes } from "@/LocalRoutes";
import { Roles } from "@/auth/user-roles";
import { cn } from "@/lib/utils";

const getNavItems = (t: TFunction) => [
    {
        title: t("routes.dashboard"),
        url: LocalRoutes.DASHBOARD,
        icon: LayoutDashboard,
        isActive: true,
        requiredRoles: [Roles.USER],
    },
    {
        title: t("routes.metering-points"),
        url: LocalRoutes.METERING_POINTS,
        icon: Cable,
        isActive: true,
        requiredRoles: [Roles.USER],
    },
    {
        title: t("routes.member-contracts"),
        url: LocalRoutes.MEMBER_CONTRACTS,
        icon: Handshake,
        isActive: true,
        requiredRoles: [Roles.USER],
    },

    {
        title: t("routes.data-exports"),
        url: LocalRoutes.DATA_EXPORTS,
        icon: FileText,
        isActive: true,
        requiredRoles: [Roles.USER],
    },
    {
        title: t("routes.admin"),
        url: "#",
        icon: Shield,
        isActive: true,
        requiredRoles: [Roles.ADMIN],
        items: [
            {
                title: t("routes.g_admin.dashboard"),
                url: LocalRoutes.ADMIN_DASHBOARD,
            },
            {
                title: t("routes.g_admin.groups.stammdaten"),
                url: "#",
                items: [
                    {
                        title: t("routes.g_admin.energy-communities"),
                        url: LocalRoutes.ADMIN_ENERGY_COMMUNITIES,
                    },
                    {
                        title: t("routes.g_admin.metering-points"),
                        url: LocalRoutes.ADMIN_METERING_POINTS,
                    },
                    {
                        title: t("routes.g_admin.legal-persons"),
                        url: LocalRoutes.ADMIN_LEGAL_PERSONS,
                    },
                    {
                        title: t("routes.g_admin.users"),
                        url: LocalRoutes.ADMIN_USERS,
                    },
                    {
                        title: t("routes.g_admin.grid-operators"),
                        url: LocalRoutes.ADMIN_GRID_OPERATORS,
                    },
                    {
                        title: t("routes.g_admin.payment-infos"),
                        url: LocalRoutes.ADMIN_PAYMENT_INFOS,
                    },
                    {
                        title: t("routes.g_admin.sepa-mandates"),
                        url: LocalRoutes.ADMIN_SEPA_MANDATES,
                    },
                ],
            },
            {
                title: t("routes.g_admin.groups.contracts"),
                url: "#",
                items: [
                    {
                        title: t(
                            "routes.g_admin.energy-community-member-contracts",
                        ),
                        url: LocalRoutes.ADMIN_ENERGY_COMMUNITY_MEMBER_CONTRACTS,
                    },
                    {
                        title: t("routes.g_admin.metering-point-contracts"),
                        url: LocalRoutes.ADMIN_METERING_POINT_CONTRACTS,
                    },
                ],
            },
            {
                title: t("routes.g_admin.groups.tariffs"),
                url: "#",
                items: [
                    {
                        title: t("routes.g_admin.tariff-components"),
                        url: LocalRoutes.ADMIN_TARIFF_COMPONENTS,
                    },
                    {
                        title: t("routes.g_admin.tariff-packages"),
                        url: LocalRoutes.ADMIN_TARIFF_PACKAGES,
                    },
                    {
                        title: t("routes.g_admin.tariff-assignments"),
                        url: LocalRoutes.ADMIN_TARIFF_ASSIGNMENTS,
                    },
                    {
                        title: t("routes.g_admin.tariff-view"),
                        url: LocalRoutes.ADMIN_TARIFF_VIEW,
                    },
                ],
            },
            {
                title: t("routes.g_admin.groups.billing"),
                url: "#",
                items: [
                    {
                        title: t("routes.g_admin.invoices"),
                        url: LocalRoutes.ADMIN_INVOICES,
                    },
                    {
                        title: t("routes.g_admin.invoice-controlling"),
                        url: LocalRoutes.ADMIN_INVOICE_CONTROLLING,
                    },
                    {
                        title: t("routes.g_admin.billing-batch-log"),
                        url: LocalRoutes.ADMIN_BILLING_BATCH_LOG,
                    },
                    {
                        title: t("routes.g_admin.bank-transactions"),
                        url: LocalRoutes.ADMIN_BANK_TRANSACTIONS,
                    },
                ],
            },
            {
                title: t("routes.g_admin.groups.support"),
                url: "#",
                items: [
                    {
                        title: t("routes.g_admin.message-interventions"),
                        url: LocalRoutes.ADMIN_MESSAGE_INTERVENTIONS,
                    },
                    {
                        title: t("routes.g_admin.onboarding-controlling"),
                        url: LocalRoutes.ADMIN_ONBOARDING_CONTROLLING,
                    },
                    {
                        title: t("routes.g_admin.notifications"),
                        url: LocalRoutes.ADMIN_NOTIFICATIONS,
                    },
                    {
                        title: t("routes.g_admin.documents"),
                        url: LocalRoutes.ADMIN_DOCUMENTS,
                    },
                ],
            },
        ],
    },
    {
        title: t("routes.root"),
        url: "#",
        icon: Shield,
        isActive: true,
        requiredRoles: [Roles.SUPER_ROOT_3000],
        items: [
            {
                title: t("routes.g_root.system-banner"),
                url: LocalRoutes.ADMIN_SYSTEM_BANNER,
            },
        ],
    },
    {
        title: t("routes.debug"),
        url: "#",
        icon: Bug,
        isActive: false,
        requiredRoles: [Roles.ROOT],
        items: [
            {
                title: t("routes.g_debug.eda-processes"),
                url: LocalRoutes.DEBUG_EDA_PROCESSES,
            },
            {
                title: t("routes.g_debug.eda-messages"),
                url: LocalRoutes.DEBUG_EDA_MESSAGES,
            },
        ],
    },
];

export const AppSidebar = ({
    ...props
}: React.ComponentProps<typeof Sidebar>) => {
    const { t } = useTranslation();
    const { state } = useSidebar();
    const isCollapsed = state === "collapsed";
    const navItems = getNavItems(t);

    return (
        <Sidebar className="select-none" collapsible="icon" {...props}>
            <SidebarHeader className="flex flex-row justify-center items-center h-[42px] overflow-hidden relative p-4">
                <img
                    alt="Logo with text"
                    className={cn(
                        "h-7 w-auto absolute transition-all duration-300",
                        isCollapsed
                            ? "opacity-0 -rotate-90 origin-left"
                            : "opacity-100 rotate-0 origin-left",
                    )}
                    src="/logo.svg"
                />
                <img
                    alt="Logo"
                    className={cn(
                        "size-7 absolute transition-all duration-300",
                        isCollapsed
                            ? "opacity-100 rotate-0 origin-center"
                            : "opacity-0 rotate-90 origin-center",
                    )}
                    src="/logo_square.svg"
                />
            </SidebarHeader>
            <SidebarContent>
                <NavMain items={navItems} />
            </SidebarContent>
            <SidebarRail />
        </Sidebar>
    );
};
